import { useAuth0 } from '@auth0/auth0-react';
import { Menu } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Drawn for the header's DeepHealth plum bar. Signed out: a Login button. Signed in: the user's initial,
 * opening a menu with who they are and Logout — signing out is rare, it does not need a button of its own in
 * the header.
 */
export function LoginButton() {
  const { t } = useTranslation();
  const { loginWithRedirect, logout, isAuthenticated, isLoading, user } =
    useAuth0();

  const doLogin = useCallback(() => {
    loginWithRedirect({
      appState: {
        returnTo: `${window.location.pathname}${window.location.search}`,
      },
    });
  }, [loginWithRedirect]);

  const doLogout = useCallback(() => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  if (!isLoading && !isAuthenticated) {
    return (
      <button
        type="button"
        className="h-10 rounded-lg border border-white/70 px-4 text-lg font-semibold text-white transition hover:bg-white hover:text-dh-plum"
        onClick={doLogin}
      >
        {t('Header:Login')}
      </button>
    );
  }
  return (
    <Menu as="div" className="relative">
      <Menu.Button
        aria-label="Account menu"
        className="flex h-10 items-center gap-1.5 rounded-lg pl-1 pr-2 text-white/80 transition hover:bg-white/10 hover:text-white"
      >
        <span
          aria-hidden
          className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-lg font-semibold uppercase text-white"
        >
          {(user?.name ?? user?.email ?? '?').charAt(0)}
        </span>
        <ChevronDownIcon aria-hidden className="h-4 w-4" />
      </Menu.Button>
      <Menu.Items className="absolute right-0 z-30 mt-2 w-64 overflow-hidden rounded-xl border border-dl-border bg-dl-surface py-1 shadow-lg focus:outline-none">
        {user?.email && (
          <div className="border-b border-dl-border px-4 py-3">
            <p className="text-sm text-dl-faint">Signed in as</p>
            <p
              className="truncate text-lg font-medium text-dl-text"
              title={user.email}
            >
              {user.email}
            </p>
          </div>
        )}
        <Menu.Item>
          {({ active }) => (
            <button
              type="button"
              onClick={doLogout}
              className={classNames(
                'block w-full px-4 py-2 text-left text-lg text-dl-text',
                {
                  'bg-dl-sunken': active,
                }
              )}
            >
              {t('Header:Logout')}
            </button>
          )}
        </Menu.Item>
      </Menu.Items>
    </Menu>
  );
}
